<script setup>
import AdminLayout from "@/Layouts/AdminLayout/AdminLayout.vue"
import { ref, computed } from "vue"
import { usePage, router, Link } from "@inertiajs/vue3"
import { useToast } from "@/composables/useToast.js"

const props = defineProps({
    users: {
        type: Object,
        required: true
    },
    stats: {
        type: Object,
        required: true
    },
    filters: {
        type: Object,
        default: () => ({})
    }
})

const page = usePage()
const { success, error } = useToast()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

const searchQuery = ref(props.filters.search || '')
const selectedPosition = ref(props.filters.position || '')
const selectedPlacement = ref(props.filters.placement || '')
const showPlacementModal = ref(false)
const showUserDetailsModal = ref(false)
const selectedUser = ref(null)
const placementForm = ref({
    user_id: null,
    parent_id: null,
    position: 'left'
})

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('en-US').format(value)
}

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?'
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)
}

const getPositionBadge = (position) => {
    if (!position) return { 
        text: 'Not Placed', 
        class: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600' 
    }
    if (position === 'left') return { 
        text: 'Left', 
        class: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-700' 
    }
    if (position === 'right') return { 
        text: 'Right', 
        class: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200 dark:border-purple-700' 
    }
    return { 
        text: position, 
        class: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600' 
    }
}

const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date)
}

const getBalanceIndicator = (leftVolume, rightVolume) => {
    const total = leftVolume + rightVolume
    if (total === 0) return { color: 'bg-gray-400', leftPercentage: 50, rightPercentage: 50, balance: 'No Activity' }
    
    const leftPercentage = (leftVolume / total) * 100
    const rightPercentage = (rightVolume / total) * 100
    
    const difference = Math.abs(leftPercentage - rightPercentage)
    
    if (difference < 10) return { color: 'bg-green-500', leftPercentage, rightPercentage, balance: 'Excellent' }
    if (difference < 25) return { color: 'bg-yellow-500', leftPercentage, rightPercentage, balance: 'Good' }
    return { color: 'bg-red-500', leftPercentage, rightPercentage, balance: 'Unbalanced' }
}

const applyFilters = () => {
    router.get('/admin/binary/users', {
        search: searchQuery.value,
        position: selectedPosition.value,
        placement: selectedPlacement.value,
        page: 1
    }, {
        preserveState: true
    })
}

const resetFilters = () => {
    searchQuery.value = ''
    selectedPosition.value = ''
    selectedPlacement.value = ''
    router.get('/admin/binary/users', {}, {
        preserveState: true
    })
}

const openUserDetails = (user) => {
    selectedUser.value = user
    showUserDetailsModal.value = true
}

const closeUserDetails = () => {
    showUserDetailsModal.value = false
    selectedUser.value = null
}

const openPlacementModal = (userId) => {
    placementForm.value = {
        user_id: userId,
        parent_id: null,
        position: 'left'
    }
    showPlacementModal.value = true
}

const closePlacementModal = () => {
    showPlacementModal.value = false
    placementForm.value = {
        user_id: null,
        parent_id: null,
        position: 'left'
    }
}

const submitPlacement = () => {
    router.post('/admin/binary/place-user', placementForm.value, {
        onSuccess: () => {
            success('User placed successfully in binary tree')
            closePlacementModal()
        },
        onError: () => {
            error('Failed to place user. Please check the parent ID and position.')
        }
    })
}

const viewUserTree = (userId) => {
    router.get(`/admin/binary/tree?user_id=${userId}`)
}

const exportUsers = () => {
    window.location.href = '/admin/binary/export'
}
</script>

<template>
    <AdminLayout title="Binary Users Management">
        <div class="space-y-6">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Binary Users</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Manage and place users in the binary network
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <button
                            @click="exportUsers"
                            class="px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 shadow-sm"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                            </svg>
                            <span class="font-medium">Export</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Stats Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-blue-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                        {{ formatNumber(stats.total_users) }}
                    </h3>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
                </div>

                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-green-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                        {{ formatNumber(stats.placed_users) }}
                    </h3>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Placed Users</p>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-500">
                        {{ ((stats.placed_users / stats.total_users) * 100).toFixed(1) }}% of total
                    </div>
                </div>

                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-orange-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                        {{ formatNumber(stats.unplaced_users) }}
                    </h3>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Unplaced Users</p>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-500">
                        Awaiting placement
                    </div>
                </div>

                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-purple-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                        {{ formatNumber(stats.active_today) }}
                    </h3>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Active Today</p>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-500">
                        Users with pairings
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Name, email or ID..."
                            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            @keyup.enter="applyFilters"
                        >
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Position</label>
                        <select
                            v-model="selectedPosition"
                            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Positions</option>
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                            <option value="null">Not Placed</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                        <select
                            v-model="selectedPlacement"
                            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            <option value="placed">Placed</option>
                            <option value="unplaced">Unplaced</option>
                        </select>
                    </div>
                    <div class="flex items-end gap-2">
                        <button
                            @click="applyFilters"
                            class="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Apply
                        </button>
                        <button
                            @click="resetFilters"
                            class="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors"
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            <!-- Users Table -->
            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead class="bg-gray-50 dark:bg-slate-700">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Parent</th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Left Volume</th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Right Volume</th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Pairs</th>
                                <th scope="col" class="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                            <tr v-if="users.data.length === 0">
                                <td colspan="7" class="px-6 py-12 text-center">
                                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                    </svg>
                                    <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">No users found</p>
                                </td>
                            </tr>
                            <tr v-for="user in users.data" :key="user.id" class="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-11 w-11">
                                            <div class="h-11 w-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                                                <span class="text-sm font-bold text-white">
                                                    {{ getInitials(user.display_name || user.name) }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ user.display_name || user.name }}</div>
                                            <div class="text-sm text-gray-500 dark:text-gray-400">{{ user.email }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="getPositionBadge(user.binary_position).class" class="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full">
                                        {{ getPositionBadge(user.binary_position).text }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    <span v-if="user.binary_parent_name" class="flex items-center">
                                        <svg class="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                        </svg>
                                        {{ user.binary_parent_name }}
                                    </span>
                                    <span v-else class="text-gray-400 dark:text-gray-500 italic">No parent</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 w-12 text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {{ formatNumber(user.left_leg_volume) }}
                                        </div>
                                        <div class="ml-2 flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                            <div 
                                                class="h-full bg-blue-500 rounded-full transition-all" 
                                                :style="{ width: Math.min((user.left_leg_volume / Math.max(user.left_leg_volume, user.right_leg_volume, 1)) * 100, 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 w-12 text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {{ formatNumber(user.right_leg_volume) }}
                                        </div>
                                        <div class="ml-2 flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                            <div 
                                                class="h-full bg-green-500 rounded-full transition-all" 
                                                :style="{ width: Math.min((user.right_leg_volume / Math.max(user.left_leg_volume, user.right_leg_volume, 1)) * 100, 100) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <svg class="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                        </svg>
                                        <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {{ formatNumber(user.total_pairs || 0) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div class="flex justify-end gap-2">
                                        <button
                                            @click="openUserDetails(user)"
                                            class="p-2 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                            </svg>
                                        </button>
                                        <button
                                            @click="viewUserTree(user.id)"
                                            class="p-2 text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                            title="View Tree"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                                            </svg>
                                        </button>
                                        <button
                                            v-if="!user.binary_parent_id"
                                            @click="openPlacementModal(user.id)"
                                            class="p-2 text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                            title="Place User"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="users.links && users.links.length > 3" class="bg-gray-50 dark:bg-slate-700 px-6 py-4 border-t border-gray-200 dark:border-slate-600">
                    <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div class="text-sm text-gray-700 dark:text-gray-300">
                            Showing <span class="font-semibold">{{ users.from }}</span> to <span class="font-semibold">{{ users.to }}</span> of <span class="font-semibold">{{ users.total }}</span> results
                        </div>
                        <div class="flex flex-wrap gap-1">
                            <component
                                v-for="(link, index) in users.links"
                                :key="index"
                                :is="link.url ? 'a' : 'span'"
                                :href="link.url"
                                v-html="link.label"
                                :class="[
                                    'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                                    link.active 
                                        ? 'bg-blue-600 text-white shadow-sm' 
                                        : link.url 
                                            ? 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600' 
                                            : 'bg-gray-100 dark:bg-slate-600 text-gray-400 cursor-not-allowed'
                                ]"
                            />
                        </div>
                    </div>
                </div>
            </div>

        <!-- Placement Modal -->
        <div v-if="showPlacementModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div @click.stop class="relative mx-auto w-full max-w-md">
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 p-6">
                    <div class="flex items-center justify-between mb-6">
                        <div class="flex items-center gap-3">
                            <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                </svg>
                            </div>
                            <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Place User in Tree</h3>
                        </div>
                        <button 
                            @click="closePlacementModal" 
                            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 p-2 rounded-lg transition-colors"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <form @submit.prevent="submitPlacement" class="space-y-5">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Parent User ID
                            </label>
                            <input
                                v-model="placementForm.parent_id"
                                type="number"
                                required
                                placeholder="Enter parent user ID"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-start">
                                <svg class="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                The ID of the parent user under whom this user will be placed
                            </p>
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Position
                            </label>
                            <div class="grid grid-cols-2 gap-3">
                                <label class="relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all"
                                    :class="placementForm.position === 'left' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'">
                                    <input type="radio" v-model="placementForm.position" value="left" class="sr-only" required>
                                    <div class="text-center">
                                        <svg class="w-8 h-8 mx-auto mb-2" :class="placementForm.position === 'left' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                        </svg>
                                        <span class="text-sm font-semibold" :class="placementForm.position === 'left' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'">Left</span>
                                    </div>
                                </label>
                                <label class="relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all"
                                    :class="placementForm.position === 'right' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'">
                                    <input type="radio" v-model="placementForm.position" value="right" class="sr-only" required>
                                    <div class="text-center">
                                        <svg class="w-8 h-8 mx-auto mb-2" :class="placementForm.position === 'right' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                                        </svg>
                                        <span class="text-sm font-semibold" :class="placementForm.position === 'right' ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'">Right</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div class="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                @click="closePlacementModal"
                                class="px-5 py-2.5 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm transition-colors"
                            >
                                Place User
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- User Details Modal -->
        <div v-if="showUserDetailsModal && selectedUser" class="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div @click.stop class="relative mx-auto w-full max-w-2xl">
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700">
                    <!-- Modal Header -->
                    <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
                        <div class="flex items-center gap-4">
                            <div class="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                                <span class="text-lg font-bold text-white">
                                    {{ getInitials(selectedUser.display_name || selectedUser.name) }}
                                </span>
                            </div>
                            <div>
                                <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ selectedUser.display_name || selectedUser.name }}</h3>
                                <p class="text-sm text-gray-500 dark:text-gray-400">{{ selectedUser.email }}</p>
                            </div>
                        </div>
                        <button 
                            @click="closeUserDetails" 
                            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 p-2 rounded-lg transition-colors"
                        >
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Modal Body -->
                    <div class="p-6 space-y-6">
                        <!-- Status Badge -->
                        <div class="flex items-center gap-2">
                            <span :class="getPositionBadge(selectedUser.binary_position).class" class="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full">
                                {{ getPositionBadge(selectedUser.binary_position).text }}
                            </span>
                            <span class="inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full"
                                :class="selectedUser.binary_parent_id ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'">
                                {{ selectedUser.binary_parent_id ? 'Placed' : 'Unplaced' }}
                            </span>
                        </div>

                        <!-- Binary Info Grid -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div class="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">User ID</p>
                                        <p class="text-lg font-bold text-gray-900 dark:text-gray-100">#{{ selectedUser.id }}</p>
                                    </div>
                                    <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                                    </svg>
                                </div>
                            </div>

                            <div class="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Parent User</p>
                                        <p class="text-lg font-bold text-gray-900 dark:text-gray-100">
                                            {{ selectedUser.binary_parent_name || 'No Parent' }}
                                        </p>
                                    </div>
                                    <svg class="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                    </svg>
                                </div>
                            </div>

                            <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Left Leg Volume</p>
                                        <p class="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                            {{ formatNumber(selectedUser.left_leg_volume) }}
                                        </p>
                                    </div>
                                    <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                    </svg>
                                </div>
                            </div>

                            <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/30 rounded-lg p-4 border border-green-200 dark:border-green-800">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Right Leg Volume</p>
                                        <p class="text-2xl font-bold text-green-900 dark:text-green-100">
                                            {{ formatNumber(selectedUser.right_leg_volume) }}
                                        </p>
                                    </div>
                                    <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                                    </svg>
                                </div>
                            </div>

                            <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/30 rounded-lg p-4 border border-purple-200 dark:border-purple-800 md:col-span-2">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">Total Binary Pairs</p>
                                        <p class="text-2xl font-bold text-purple-900 dark:text-purple-100">
                                            {{ formatNumber(selectedUser.total_pairs || 0) }}
                                        </p>
                                        <p class="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                            Matched pairs from left and right legs
                                        </p>
                                    </div>
                                    <svg class="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Footer -->
                    <div class="flex items-center justify-between p-6 bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-700 rounded-b-xl">
                        <button
                            @click="viewUserTree(selectedUser.id)"
                            class="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium shadow-sm transition-colors flex items-center gap-2"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                            </svg>
                            View Binary Tree
                        </button>
                        <button
                            v-if="!selectedUser.binary_parent_id"
                            @click="openPlacementModal(selectedUser.id); closeUserDetails();"
                            class="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-sm transition-colors flex items-center gap-2"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                            </svg>
                            Place User
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </div><!-- end space-y-6 -->
    </AdminLayout>
</template>
